import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

// Helper: normaliza valores numéricos (aceita vírgula, string, number)
function normalizeNumeric(value: any): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') {
    const cleaned = value.replace(',', '.').trim();
    const parsed = parseFloat(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  return null;
}

// Helper: normaliza valores inteiros (aceita vírgula, string, number)
function normalizeInt(value: any): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') {
    const cleaned = value.replace(',', '.').trim();
    const parsed = parseInt(cleaned);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= 0 ? Math.floor(value) : null;
  }
  return null;
}

// Helper: normaliza datas (aceita string, Date, null)
function normalizeDate(value: any): string | null {
  if (value === null || value === undefined || value === '') return null;
  
  if (value instanceof Date && !isNaN(value.getTime())) {
    return value.toISOString().split('T')[0];
  }
  
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    
    // Já está no formato YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    
    // Tenta parsear outros formatos
    const date = new Date(trimmed);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
  }
  
  throw new Error('Data inválida. Use o formato AAAA-MM-DD.');
}

// UUID validation regex
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface MaintenancePlan {
  id: string;
  company_id: string;
  vehicle_id: string;
  name: string;
  type: string;
  maintenance_category?: string;
  periodicity_days?: number;
  periodicity_km?: number;
  next_execution_date?: string;
  next_execution_km?: number;
  status: string;
  approval_status?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
  rejection_reason?: string | null;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
  fleet_vehicles?: {
    id: string;
    plate: string;
    make?: string;
    model?: string;
    odometer?: number;
  };
  displayStatus?: string; // Calculated status based on work orders
}

// Helper function to calculate dynamic status based on work orders
export function calculatePlanStatus(
  originalStatus: string,
  planId: string,
  workOrders: Array<{ plan_id: string | null; status: string }>
): string {
  // Se o status original é pending, inactive ou completed, mantém
  if (originalStatus === 'pending' || originalStatus === 'inactive' || originalStatus === 'completed') {
    return originalStatus;
  }
  
  // Verifica se há OSs em andamento vinculadas a este plano
  const hasInProgressOrders = workOrders.some(
    wo => wo.plan_id === planId && wo.status === 'in_progress'
  );
  
  if (hasInProgressOrders) {
    return 'in_progress';
  }
  
  return originalStatus; // Retorna o status original (active ou pending)
}

// Helper function to check if a plan has open work orders
export async function planHasOpenWorkOrders(planId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from("fleet_work_orders")
    .select("id")
    .eq("plan_id", planId)
    .in("status", ["open", "in_progress"])
    .limit(1);
  
  if (error) {
    console.error("Error checking open work orders:", error);
    return false;
  }
  
  return (data?.length || 0) > 0;
}

// Helper function to check if a plan is nearing expiration
export function isPlanNearingExpiration(plan: MaintenancePlan): boolean {
  // Verificar por data (30 dias)
  if (plan.next_execution_date) {
    const nextDate = new Date(plan.next_execution_date);
    const today = new Date();
    const diffTime = nextDate.getTime() - today.getTime();
    const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (daysRemaining >= 0 && daysRemaining <= 30) {
      return true;
    }
  }
  
  // Verificar por KM (1000 km)
  if (plan.next_execution_km !== null && plan.next_execution_km !== undefined) {
    const currentOdometer = plan.fleet_vehicles?.odometer || 0;
    const kmRemaining = plan.next_execution_km - currentOdometer;
    
    if (kmRemaining >= 0 && kmRemaining <= 1000) {
      return true;
    }
  }
  
  return false;
}

export function useMaintenancePlans(companyId?: string) {
  return useQuery({
    queryKey: ["maintenancePlans", companyId],
    queryFn: async () => {
      if (!companyId) return [];
      const { data, error } = await supabase
        .from("fleet_maint_plans")
        .select(`
          *,
          fleet_vehicles(id, plate, make, model, odometer)
        `)
        .eq("company_id", companyId)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!companyId,
  });
}

export function useCreateMaintenancePlan(companyId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (planData: Omit<MaintenancePlan, 'id' | 'created_at' | 'updated_at' | 'company_id' | 'fleet_vehicles' | 'approval_status' | 'approved_by' | 'approved_at' | 'rejection_reason'>) => {
      // Novos planos sempre começam como "pendentes" de aprovação
      // O status operacional (ativo/inativo) é definido pelo usuário
      const { data, error } = await supabase
        .from("fleet_maint_plans")
        .insert([{ 
          ...planData, 
          company_id: companyId,
          approval_status: 'pending',
          status: planData.status || 'inactive'
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maintenancePlans", companyId] });
      queryClient.invalidateQueries({ queryKey: ["pendingPlans", companyId] });
      toast({
        title: "Sucesso",
        description: "Plano criado e enviado para aprovação.",
      });
    },
    onError: (error) => {
      console.error("Error creating maintenance plan:", error);
      toast({
        title: "Erro",
        description: "Erro ao criar plano de manutenção.",
        variant: "destructive",
      });
    },
  });
}

export function useUpdateMaintenancePlan(companyId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...planData }: Partial<MaintenancePlan> & { id: string }) => {
      // If trying to change status to inactive or completed, check for open work orders
      if (planData.status && (planData.status === 'inactive' || planData.status === 'completed')) {
        const hasOpenOrders = await planHasOpenWorkOrders(id);
        
        if (hasOpenOrders) {
          throw new Error("Não é possível alterar o status do plano enquanto houver ordens de serviço abertas ou em andamento.");
        }
      }

      // Limpar campos vazios e undefined
      const cleanedData = { ...planData };
      
      Object.keys(cleanedData).forEach(key => {
        const value = cleanedData[key as keyof typeof cleanedData];
        // Remove undefined, 'undefined', empty strings and null
        if (value === undefined || value === 'undefined' || value === '' || value === null) {
          delete cleanedData[key as keyof typeof cleanedData];
        }
      });
      
      // Validar e normalizar campos
      const VALID_STATUSES = ['pending', 'active', 'inactive', 'completed'];
      const VALID_TYPES = ['preventive', 'corrective'];
      const VALID_CATEGORIES = ['mechanical', 'tire', 'suspension', 'electrical', 'bodywork', 'other'];
      const VALID_APPROVAL_STATUS = ['pending', 'approved', 'rejected'];
      
      // Validar status
      if ('status' in cleanedData && cleanedData.status) {
        if (!VALID_STATUSES.includes(cleanedData.status)) {
          throw new Error(`Status inválido. Valores válidos: Pendente, Ativo, Inativo, Concluído.`);
        }
      }
      
      // Validar type
      if ('type' in cleanedData && cleanedData.type) {
        if (!VALID_TYPES.includes(cleanedData.type)) {
          throw new Error(`Tipo inválido. Valores válidos: Preventiva, Corretiva.`);
        }
      }
      
      // Validar maintenance_category
      if ('maintenance_category' in cleanedData && cleanedData.maintenance_category) {
        if (!VALID_CATEGORIES.includes(cleanedData.maintenance_category)) {
          throw new Error(`Categoria inválida. Use: Mecânica, Pneus, Suspensão, Elétrica, Funilaria e Pintura ou Outros.`);
        }
      }
      
      // Validar approval_status
      if ('approval_status' in cleanedData && cleanedData.approval_status) {
        if (!VALID_APPROVAL_STATUS.includes(cleanedData.approval_status)) {
          throw new Error(`Status de aprovação inválido.`);
        }
      }
      
      // Validar name
      if ('name' in cleanedData) {
        const name = typeof cleanedData.name === 'string' ? cleanedData.name.trim() : '';
        if (name === '') {
          throw new Error('Nome do plano não pode ser vazio.');
        }
        cleanedData.name = name;
      }
      
      // Validar vehicle_id
      if ('vehicle_id' in cleanedData && cleanedData.vehicle_id) {
        if (!UUID_REGEX.test(cleanedData.vehicle_id)) {
          throw new Error('ID do veículo inválido.');
        }
      }
      
      // Normalizar campos numéricos (aceita vírgula como decimal)
      if ('periodicity_days' in cleanedData) {
        cleanedData.periodicity_days = normalizeInt(cleanedData.periodicity_days);
      }
      
      if ('periodicity_km' in cleanedData) {
        cleanedData.periodicity_km = normalizeNumeric(cleanedData.periodicity_km);
      }
      
      if ('next_execution_km' in cleanedData) {
        cleanedData.next_execution_km = normalizeNumeric(cleanedData.next_execution_km);
      }
      
      // Normalizar data
      if ('next_execution_date' in cleanedData) {
        try {
          cleanedData.next_execution_date = normalizeDate(cleanedData.next_execution_date);
        } catch (error: any) {
          throw new Error(error.message || 'Data inválida. Use o formato AAAA-MM-DD.');
        }
      }
      
      // Remove empty UUID fields explicitly and validate format
      const uuidFields = ['vehicle_id', 'created_by', 'updated_by', 'approved_by'] as const;
      uuidFields.forEach(field => {
        if (field in cleanedData) {
          const value = (cleanedData as any)[field];
          if (value === '' || value === null || value === undefined) {
            delete (cleanedData as any)[field];
          } else if (typeof value === 'string' && !UUID_REGEX.test(value)) {
            throw new Error(`${field} com formato inválido de UUID.`);
          }
        }
      });
      
      // Verificar se há mudanças para aplicar
      const fieldsToUpdate = Object.keys(cleanedData).filter(
        key => key !== 'id' && key !== 'company_id' && key !== 'created_at' && key !== 'created_by'
      );
      
      if (fieldsToUpdate.length === 0) {
        throw new Error('Nenhuma alteração para salvar.');
      }
      
      console.log('🔧 Dados originais:', planData);
      console.log('🧹 Dados limpos:', cleanedData);
      console.log('🆔 Campos UUID:', {
        vehicle_id: cleanedData.vehicle_id || 'não enviado',
        created_by: cleanedData.created_by || 'não enviado',
        updated_by: cleanedData.updated_by || 'não enviado',
        approved_by: cleanedData.approved_by || 'não enviado'
      });
      console.log('📊 Tipos dos campos numéricos:', {
        periodicity_days: typeof cleanedData.periodicity_days,
        periodicity_km: typeof cleanedData.periodicity_km,
        next_execution_km: typeof cleanedData.next_execution_km,
        status: cleanedData.status
      });

      const { data, error } = await supabase
        .from("fleet_maint_plans")
        .update(cleanedData)
        .eq("id", id)
        .eq("company_id", companyId)
        .select()
        .single();
      
      if (error) {
        // Mensagens específicas para erros do banco
        if (error.message.includes('maintenance_category_check')) {
          throw new Error('Categoria inválida. Use: Mecânica, Pneus, Suspensão, Elétrica, Funilaria e Pintura ou Outros.');
        }
        if (error.message.includes('approval_status_check')) {
          throw new Error('Status de aprovação inválido.');
        }
        throw error;
      }
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maintenancePlans", companyId] });
      toast({
        title: "Sucesso",
        description: "Plano de manutenção atualizado com sucesso.",
      });
    },
    onError: (error: any) => {
      console.error("Error updating maintenance plan:", error);
      toast({
        title: "Erro",
        description: error.message || "Erro ao atualizar plano de manutenção.",
        variant: "destructive",
      });
    },
  });
}

export function useDeleteMaintenancePlan(companyId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (planId: string) => {
      const { error } = await supabase
        .from("fleet_maint_plans")
        .delete()
        .eq("id", planId)
        .eq("company_id", companyId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maintenancePlans", companyId] });
      toast({
        title: "Sucesso",
        description: "Plano de manutenção excluído com sucesso.",
      });
    },
    onError: (error) => {
      console.error("Error deleting maintenance plan:", error);
      toast({
        title: "Erro",
        description: "Erro ao excluir plano de manutenção.",
        variant: "destructive",
      });
    },
  });
}